// Text manipulation utilities

/**
 * Get information about the current paragraph based on cursor position
 */
export const getCurrentParagraphInfo = (text, cursorPosition) => {
  const lines = text.split('\n');
  let currentLineIndex = 0;
  let charCount = 0;
  
  for (let i = 0; i < lines.length; i++) {
    if (charCount + lines[i].length + 1 > cursorPosition) {
      currentLineIndex = i;
      break;
    }
    charCount += lines[i].length + 1; // +1 for newline character
  }
  
  return {
    lineIndex: currentLineIndex,
    lineText: lines[currentLineIndex] || '',
    totalLines: lines.length
  };
};

/**
 * Copy text to clipboard
 */
export const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback for older browsers or non-secure contexts
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const result = document.execCommand('copy');
      document.body.removeChild(textArea);
      return result;
    }
  } catch (error) {
    console.error('Failed to copy text:', error);
    return false;
  }
};

/**
 * Extract plain text from HTML content
 */
export const extractTextFromHtml = (html) => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  return tempDiv.textContent || tempDiv.innerText || '';
};

/**
 * Get word count from text
 */
export const getWordCount = (text) => {
  return text.trim().split(/\s+/).filter(word => word.length > 0).length;
};

/**
 * Get character count (excluding spaces)
 */
export const getCharacterCount = (text) => {
  return text.replace(/\s/g, '').length;
};

/**
 * Estimate reading time in minutes
 */
export const getReadingTime = (text) => {
  const wordsPerMinute = 200;
  const wordCount = getWordCount(text);
  return Math.ceil(wordCount / wordsPerMinute);
};

/**
 * Clean and format text for better readability
 */
export const formatText = (text) => {
  return text
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .replace(/\n\s*\n/g, '\n\n') // Ensure proper paragraph spacing
    .trim();
};

/**
 * Validate if text is suitable for AI processing
 */
export const isValidForAI = (text) => {
  const trimmedText = text.trim();
  return trimmedText.length >= 10 && trimmedText.length <= 50000;
};

/**
 * Truncate text to specified length with ellipsis
 */
export const truncateText = (text, maxLength = 100) => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
}; 